import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface DocuSignSignerInfo {
  signerName: string;
  signerEmail: string;
  pageNumber: string;
  xCordinate: string;
  yCordinate: string;
  routingOrder?: number;
}

export interface DocuSignMultiSignerPayload {
  Signatories: {
    SignerName: string;
    SignerEmail: string;
    PageNumber: string;
    XCoordinate: string;
    YCoordinate: string;
    RoutingOrder: number;
  }[];
}

export interface DocuSignSendRequest {
  tenantId: number;
  contractId: number;
  signerName: string;
  signerEmail: string;
  pageNumber: string;
  xCordinate: string;
  yCordinate: string;
}

export interface DocuSignSendMultipleRequest {
  tenantId: number;
  contractId: number;
  signers: DocuSignSignerInfo[];
}

export interface ApiResponse<T = any> {
  isSuccess: boolean;
  status: number;
  message: string;
  body: T | null;
}

export interface DocuSignSigner {
  name: string;
  email: string;
  status: string;
  signedDateTime?: string;
  declinedDateTime?: string;
  declinedReason?: string;
  deliveredDateTime?: string;
  sentDateTime?: string;
  recipientId?: string;
  routingOrder?: string;
  note?: string;
}

export interface DocuSignRecipients {
  signers: DocuSignSigner[];
  agents?: any[];
  carbonCopies?: any[];
  certifiedDeliveries?: any[];
  editors?: any[];
  inPersonSigners?: any[];
  intermediaries?: any[];
  witnesses?: any[];
  seals?: any[];
  currentRoutingOrder?: string;
  recipientCount?: string;
  errorDetails?: any;
}

export interface DocuSignWorkflowInfo {
  envelopeId: string;
  status: string;
  statusChangedDateTime: string;
  recipients: DocuSignRecipients;
  // Optional reason when an envelope is voided (recalled)
  voidedReason?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DocuSignService {
  private baseUrl: string;

  constructor(
    private http: HttpClient
  ) {
    const apiBase = environment.contractApiURL;
    
    const cleanBase = apiBase.replace(/\/api1\/?$/, '');
    this.baseUrl = `${cleanBase}/docusign`;
  }

  /**
   * Get Access Token
   * GET /api1/docusign/token
   */
  getAccessToken(): Observable<ApiResponse<string>> {
    return this.http.get<ApiResponse<string>>(`${this.baseUrl}/token`);
  }

  /**
   * Send Document for Signature (Single Signer)
   * POST /api1/docusign/signature
   * Content-Type: application/x-www-form-urlencoded
   */
  sendDocumentForSignature(request: DocuSignSendRequest): Observable<ApiResponse<string>> {
    const formData = new URLSearchParams();
    
    formData.append('tenantId', request.tenantId.toString());
    formData.append('contractId', request.contractId.toString());
    formData.append('signerName', (request.signerName || '').trim());
    formData.append('signerEmail', (request.signerEmail || '').trim());
    formData.append('pageNumber', (request.pageNumber || '1').trim());
    formData.append('xCordinate', (request.xCordinate || '0').trim());
    formData.append('yCordinate', (request.yCordinate || '0').trim());

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<ApiResponse<string>>(`${this.baseUrl}/signature`, formData.toString(), { headers });
  }

  /**
   * Send Document for Signature (Multiple Signers)
   * POST /api1/docusign/signature?tenantId={tenantId}&contractId={contractId}
   * Content-Type: application/json
   */
  sendDocumentForSignatureMultiple(
    tenantId: number,
    contractId: number,
    payload: DocuSignMultiSignerPayload
  ): Observable<ApiResponse<string>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<ApiResponse<string>>(
      `${this.baseUrl}/signature?tenantId=${tenantId}&contractId=${contractId}`,
      payload,
      { headers }
    );
  }

  /**
   * Get Workflow Info
   * GET /api1/docusign/workflowInfo?tenantId={tenantId}&contractId={contractId}
   */
  getWorkflowInfo(tenantId: number, contractId: number): Observable<ApiResponse<DocuSignWorkflowInfo>> {
    return this.http.get<ApiResponse<DocuSignWorkflowInfo>>(
      `${this.baseUrl}/workflowInfo?tenantId=${tenantId}&contractId=${contractId}`
    );
  }

  /**
   * Void Envelope
   * POST /api1/docusign/voidEnvelope?tenantId={tenantId}&contractId={contractId}&voidReason={voidReason}
   */
  voidEnvelope(tenantId: number, contractId: number, voidReason: string = 'void contract test'): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.baseUrl}/voidEnvelope?tenantId=${tenantId}&contractId=${contractId}&voidReason=${encodeURIComponent(voidReason)}`,
      {}
    );
  }

  /**
   * Store Signed Document
   * POST /api1/docusign/storeSignedDocument?tenantId={tenantId}&contractId={contractId}
   * Asks the backend to pull the completed envelope's signed PDF from DocuSign
   * and attach it to the contract. Same result as the Connect webhook, but
   * driven by the app so it does not depend on DocuSign calling us.
   */
  storeSignedDocument(tenantId: number, contractId: number): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(
      `${this.baseUrl}/storeSignedDocument?tenantId=${tenantId}&contractId=${contractId}`,
      {}
    );
  }

}

